/**
 * Utility for generating user-friendly error messages from Supabase errors
 */

type ActionType = "criar" | "atualizar" | "excluir" | "carregar";

interface EntityConfig {
  singular: string;
  plural: string;
  article: "o" | "a";
}

const ENTITY_LABELS: Record<string, EntityConfig> = {
  product: { singular: "produto", plural: "produtos", article: "o" },
  category: { singular: "categoria", plural: "categorias", article: "a" },
  ingredient: { singular: "ingrediente", plural: "ingredientes", article: "o" },
  payment_method: { singular: "forma de pagamento", plural: "formas de pagamento", article: "a" },
  table: { singular: "mesa", plural: "mesas", article: "a" },
  order: { singular: "pedido", plural: "pedidos", article: "o" },
  command: { singular: "comanda", plural: "comandas", article: "a" },
  printer: { singular: "impressora", plural: "impressoras", article: "a" },
  production_location: { singular: "local de produção", plural: "locais de produção", article: "o" },
  team_member: { singular: "membro da equipe", plural: "membros da equipe", article: "o" },
  cash_register: { singular: "caixa", plural: "caixas", article: "o" },
  stock_movement: { singular: "movimentação de estoque", plural: "movimentações de estoque", article: "a" },
  entry_type: { singular: "tipo de entrada", plural: "tipos de entrada", article: "o" },
};

/**
 * Get a user-friendly error message from a Supabase error
 */
export function getErrorMessage(
  error: any,
  action: ActionType,
  entityType: string = "item"
): string {
  const errorCode = error?.code;
  const errorMessage = error?.message || "";
  const entity = ENTITY_LABELS[entityType] || { singular: entityType, plural: entityType + "s", article: "o" };

  // Duplicate key violation (23505)
  if (errorCode === "23505") {
    if (errorMessage.includes("barcode")) {
      return "Já existe um produto com este código de barras";
    }
    if (errorMessage.includes("name")) {
      return `Já existe ${entity.article === "o" ? "um" : "uma"} ${entity.singular} com este nome`;
    }
    if (errorMessage.includes("number")) {
      return `Este número já está em uso`;
    }
    if (errorMessage.includes("slug")) {
      return "Este identificador já está em uso";
    }
    return "Este registro já existe no sistema";
  }

  // RLS policy violation (42501)
  if (errorCode === "42501") {
    return `Você não tem permissão para ${action} ${entity.plural}`;
  }

  // Not null violation (23502)
  if (errorCode === "23502") {
    const columnMatch = errorMessage.match(/column "(\w+)"/);
    if (columnMatch) {
      const columnName = columnMatch[1];
      const fieldLabels: Record<string, string> = {
        name: "nome",
        price: "preço",
        quantity: "quantidade",
        amount: "valor",
        type: "tipo",
        unit: "unidade",
      };
      const fieldLabel = fieldLabels[columnName] || columnName;
      return `O campo "${fieldLabel}" é obrigatório`;
    }
    return "Preencha todos os campos obrigatórios";
  }

  // Foreign key violation (23503)
  if (errorCode === "23503") {
    if (action === "excluir") {
      return `Est${entity.article} ${entity.singular} não pode ser excluíd${entity.article} pois está sendo usad${entity.article} em outros registros`;
    }
    return "Um dos campos selecionados não existe mais no sistema";
  }

  // Check constraint violation (23514)
  if (errorCode === "23514") {
    if (errorMessage.includes("price") || errorMessage.includes("amount")) {
      return "O valor não pode ser negativo";
    }
    if (errorMessage.includes("quantity") || errorMessage.includes("stock")) {
      return "A quantidade não pode ser negativa";
    }
    return "Valor inválido para um dos campos";
  }

  // String data too long (22001)
  if (errorCode === "22001") {
    return "Um dos campos excede o tamanho máximo permitido";
  }

  // Invalid input syntax (22P02)
  if (errorCode === "22P02") {
    return "Formato de dado inválido. Verifique os campos preenchidos";
  }

  // Connection issues
  if (errorMessage.includes("Failed to fetch") || errorMessage.includes("NetworkError")) {
    return "Erro de conexão. Verifique sua internet e tente novamente";
  }

  // Timeout
  if (errorMessage.includes("timeout") || errorMessage.includes("Timeout")) {
    return "A operação demorou muito. Tente novamente";
  }

  // Generic fallback
  return `Erro ao ${action} ${entity.singular}. Tente novamente.`;
}

/**
 * Log error with context for debugging
 */
export function logError(error: any, context: string): void {
  console.error(`[${context}] Error:`, {
    code: error?.code,
    message: error?.message,
    details: error?.details,
    hint: error?.hint,
  });
}
